import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { evaluate, parse, format as mathFormat } from 'mathjs';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { useRegisterCommands, useRegisterPage, usePluginNavigation, useRegisterShortcut, useDebouncedSearch } from '../hooks';
import type { Command as CommandType } from '../types';
import styles from './CalculatorPlugin.module.scss';
import { useNotify } from '@/common/notifications';

const PLUGIN_ID = 'calculator-plugin';
const CALC_PAGE = 'calculator';
const CALC_PLACEHOLDER = 'Enter expression… e.g. 2 + 2, sqrt(16), sin(pi/2)';

// ---------------------------------------------------------------------------
// formatResult — format mathjs output for display
// ---------------------------------------------------------------------------

function formatResult(value: unknown): string {
  if (typeof value === 'number') {
    // Use mathjs format for clean representation (avoids floating point noise)
    const formatted = mathFormat(value, { precision: 14 });
    // Remove trailing zeros after decimal
    return formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value === null || value === undefined) return '';
  // Handle complex numbers, matrices etc.
  return mathFormat(value as any, { precision: 10 });
}

// ---------------------------------------------------------------------------
// expressionToLatex — convert a mathjs expression string to LaTeX via mathjs
// parser, falls back to the raw string on parse error
// ---------------------------------------------------------------------------

function expressionToLatex(expr: string): string | null {
  const trimmed = expr.trim();
  if (!trimmed) return null;
  try {
    return parse(trimmed).toTex({ parenthesis: 'auto', implicit: 'show' });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// KatexExpression — renders a LaTeX string using KaTeX
// ---------------------------------------------------------------------------

const KatexExpression: React.FC<{ latex: string }> = ({ latex }) => {
  const html = useMemo(() => {
    try {
      return katex.renderToString(latex, {
        displayMode: true,
        throwOnError: false,
        trust: false,
      });
    } catch {
      return null;
    }
  }, [latex]);

  if (!html) return <span className={styles.expressionFallback}>{latex}</span>;

  return (
    <div
      className={styles.katexDisplay}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// ---------------------------------------------------------------------------
// useCalculator — evaluates expression, returns result or error
// ---------------------------------------------------------------------------

interface CalcResult {
  value: string | null;
  isError: boolean;
}

function useCalculator(expression: string): CalcResult {
  return useMemo((): CalcResult => {
    const trimmed = expression.trim();
    if (!trimmed) return { value: null, isError: false };
    try {
      const result = evaluate(trimmed);
      // Skip non-numeric-ish output (e.g. functions that return nothing)
      if (result === undefined) return { value: null, isError: false };
      return { value: formatResult(result), isError: false };
    } catch {
      return { value: null, isError: true };
    }
  }, [expression]);
}

// ---------------------------------------------------------------------------
// CalcDisplay — shared UI: expression on the left, result on the right
// ---------------------------------------------------------------------------

interface CalcDisplayProps {
  expression: string;
  latex: string | null;
  result: string | null;
  isError: boolean;
}

const CalcDisplay: React.FC<CalcDisplayProps> = ({ expression, latex, result, isError }) => {
  const hasExpression = expression.trim().length > 0;
  return (
    <div className={styles.calcDisplay}>
      {/* Left: rendered expression */}
      <div className={styles.expression}>
        {latex != null
          ? <KatexExpression latex={latex} />
          : hasExpression
            ? <div className={styles.expressionFallback}>{expression}</div>
            : <div className={styles.placeholder}>Type a math expression to evaluate…</div>
        }
      </div>

      {/* Divider */}
      <div className={styles.divider} />

      {/* Right: result */}
      <div className={styles.result}>
        {result != null && !isError && (
          <div className={styles.resultValue}>{result}</div>
        )}
        {isError && hasExpression && (
          <div className={styles.resultError}>…</div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// InlineCalcRenderer — render prop component for the inline list command.
// Reads live searchValue from context so it stays responsive.
// ---------------------------------------------------------------------------

const InlineCalcRenderer: React.FC = () => {
  const { searchValue } = useCommandPaletteContext();
  const latex = useMemo(() => expressionToLatex(searchValue), [searchValue]);
  const { value: result, isError } = useCalculator(searchValue);
  return <CalcDisplay expression={searchValue} latex={latex} result={result} isError={isError} />;
};

// ---------------------------------------------------------------------------
// useInlineCalculatorCommand — registers a top-scored inline command whenever
// the debounced search value is a valid, evaluable math expression.
// ---------------------------------------------------------------------------

const INLINE_COMMAND_ID = 'calculator-inline-result';

function useInlineCalculatorCommand(pluginId: string): void {
  const notify = useNotify();
  const { close } = useCommandPaletteContext();
  const debouncedSearch = useDebouncedSearch(150);
  const { value: result } = useCalculator(debouncedSearch);

  const inlineCommands = useMemo((): CommandType[] => {
    if (!debouncedSearch.trim() || result == null) return [];
    return [
      {
        id: INLINE_COMMAND_ID,
        label: debouncedSearch.trim(),
        description: `= ${result}`,
        category: 'Calculator',
        scoreOverride: 9999,
        render: InlineCalcRenderer,
        primary: {
          id: `${INLINE_COMMAND_ID}-copy`,
          label: 'Copy result',
          action: () => {
            navigator.clipboard.writeText(result);
            notify('Result copied to clipboard!');
            close();
          },
        },
      },
    ];
  }, [debouncedSearch, result]);

  useRegisterCommands({ pluginId, commands: inlineCommands });
}

// ---------------------------------------------------------------------------
// CalculatorPage — rendered inside the palette when on CALC_PAGE
// ---------------------------------------------------------------------------

const CalculatorPage: React.FC = () => {
  const { searchValue } = useCommandPaletteContext();
  const { value: result, isError } = useCalculator(searchValue);
  const [copied, setCopied] = useState(false);

  const copyResult = useCallback(() => {
    if (result == null) return;
    navigator.clipboard.writeText(result).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [result]);

  // Allow pressing Enter to copy when result is available
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && result != null && !isError) {
        e.preventDefault();
        copyResult();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [result, isError, copyResult]);

  const latex = useMemo(() => expressionToLatex(searchValue), [searchValue]);

  return (
    <>
      <div className={styles.calcPage}>
        <CalcDisplay expression={searchValue} latex={latex} result={result} isError={isError} />
      </div>
      {result != null && !isError && (
        <div className={styles.copyHint}>
          <span className={copied ? styles.copied : undefined}>
            {copied ? 'Copied!' : 'Enter to copy result'}
          </span>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// CalculatorPlugin — registers the global command and page
// ---------------------------------------------------------------------------

export const CalculatorPlugin: React.FC = () => {
  const { navigateTo, openAndNavigate } = usePluginNavigation(PLUGIN_ID);

  const globalCommands = useMemo((): CommandType[] => [
    {
      id: 'open-calculator',
      label: 'Calculator',
      description: 'Evaluate a math expression',
      category: 'Tools',
      shortcut: 'mod+shift+c',
      keywords: ['experimental', 'calculator', 'math', 'calculate', 'expression', 'evaluate', 'compute', 'arithmetic'],
      primary: {
        id: 'open-calculator-action',
        label: 'Open Calculator',
        action: () => navigateTo(CALC_PAGE, CALC_PLACEHOLDER),
      },
    },
  ], [navigateTo]);

  useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
  useRegisterPage({ pluginId: PLUGIN_ID, pageId: CALC_PAGE, component: CalculatorPage });
  useInlineCalculatorCommand(PLUGIN_ID + "-inline");

  useRegisterShortcut({
    shortcut: 'mod+shift+c',
    label: 'Open Calculator',
    handler: () => openAndNavigate(CALC_PAGE, CALC_PLACEHOLDER),
  });

  return null;
};
